interface KpiCardProps {
  title: string
  value: string
  trend: 'up' | 'down' | 'stable'
  trendPercent: number
  icon: string
}

// Material Symbols Outlined path data (self-hosted, same rationale as
// AppShell.tsx's Icon: viewBox fixed at 0 -960 960 960, filled shapes, no
// webfont). Keyed by the icon's own Material Symbols name rather than a
// separate semantic key -- the old droplet/gauge/alert keys were each reused
// across unrelated KPI tiles in ExecutiveDashboard.tsx/InfrastructureFocus.tsx
// (e.g. "droplet" meant both "Zone count" and "total meters"), which is
// exactly the kind of icon-meaning drift this pass fixes; each call site now
// names the icon it actually wants.
const iconPaths: Record<string, { path: string; bg: string; fg: string }> = {
  readiness_score: {
    path: 'M295-119q-36-1-68.5-18.5T165-189q-40-48-62.5-114.5T80-440q0-83 31.5-156T197-723q54-54 127-85.5T480-840q83 0 156 32t127 87q54 55 85.5 129T880-433q0 77-25 144t-71 113q-28 28-59 42.5T662-119q-18 0-36-4.5T590-137l-56-28q-12-6-25.5-9t-28.5-3q-15 0-28.5 3t-25.5 9l-56 28q-19 10-37.5 14.5T295-119Zm2-80q9 0 18.5-2t18.5-7l56-28q21-11 43.5-16t45.5-5q23 0 46 5t44 16l57 28q9 5 18 7t18 2q19 0 36-10t34-30q32-38 50-91t18-109q0-134-93-227.5T480-760q-134 0-227 94t-93 228q0 57 18.5 111t51.5 91q17 20 33 28.5t34 8.5Zm183-281Zm0 120q33 0 56.5-23.5T560-440q0-8-1.5-16t-4.5-16l50-67q10 13 17.5 27.5T634-480h82q-15-88-81.5-144T480-680q-88 0-155 56.5T244-480h82q14-54 57-87t97-33q17 0 32 3t29 9l-51 69q-2 0-5-.5t-5-.5q-33 0-56.5 23.5T400-440q0 33 23.5 56.5T480-360Z',
    bg: 'bg-blue-50', fg: 'text-brand-cyan-text',
  },
  sensors: {
    path: 'M197-197q-54-55-85.5-127.5T80-480q0-84 31.5-156.5T197-763l57 57q-44 44-69 102t-25 124q0 67 25 125t69 101l-57 57Zm113-113q-32-33-51-76.5T240-480q0-51 19-94.5t51-75.5l57 57q-22 22-34.5 51T320-480q0 33 12.5 62t34.5 51l-57 57Zm170-90q-33 0-56.5-23.5T400-480q0-33 23.5-56.5T480-560q33 0 56.5 23.5T560-480q0 33-23.5 56.5T480-400Zm170 90-57-57q22-22 34.5-51t12.5-62q0-33-12.5-62T593-593l57-57q32 32 51 75.5t19 94.5q0 50-19 93.5T650-310Zm113 113-57-57q44-44 69-102t25-124q0-67-25-125t-69-101l57-57q54 54 85.5 126.5T880-480q0 83-31.5 155.5T763-197Z',
    bg: 'bg-blue-50', fg: 'text-brand-cyan-text',
  },
  speed: {
    path: 'M418-340q24 24 62 23.5t56-27.5l224-336-336 224q-27 18-28.5 55t22.5 61Zm62-460q59 0 113.5 16.5T696-734l-76 48q-33-17-68.5-25.5T480-720q-133 0-226.5 93.5T160-400q0 42 11.5 83t32.5 77h552q23-38 33.5-79t10.5-85q0-36-8.5-70T766-540l48-76q30 47 47.5 100T880-406q1 57-13 109t-41 99q-11 18-30 28t-40 10H204q-21 0-40-10t-30-28q-26-45-40-95.5T80-400q0-83 31.5-155.5t86-127Q252-737 325-768.5T480-800Zm7 313Z',
    bg: 'bg-blue-50', fg: 'text-brand-cyan-text',
  },
  percent: {
    path: 'M300-520q-58 0-99-41t-41-99q0-58 41-99t99-41q58 0 99 41t41 99q0 58-41 99t-99 41Zm0-80q25 0 42.5-17.5T360-660q0-25-17.5-42.5T300-720q-25 0-42.5 17.5T240-660q0 25 17.5 42.5T300-600Zm360 440q-58 0-99-41t-41-99q0-58 41-99t99-41q58 0 99 41t41 99q0 58-41 99t-99 41Zm0-80q25 0 42.5-17.5T720-300q0-25-17.5-42.5T660-360q-25 0-42.5 17.5T600-300q0 25 17.5 42.5T660-240Zm-444 80-56-56 584-584 56 56-584 584Z',
    bg: 'bg-blue-50', fg: 'text-brand-cyan-text',
  },
  layers: {
    path: 'M480-118 120-398l66-50 294 228 294-228 66 50-360 280Zm0-202L120-600l360-280 360 280-360 280Zm0-280Zm0 178 230-178-230-178-230 178 230 178Z',
    bg: 'bg-blue-50', fg: 'text-brand-cyan-text',
  },
  warning: {
    path: 'm40-120 440-760 440 760H40Zm138-80h604L480-720 178-200Zm302-40q17 0 28.5-11.5T520-280q0-17-11.5-28.5T480-320q-17 0-28.5 11.5T440-280q0 17 11.5 28.5T480-240Zm-40-120h80v-200h-80v200Zm40-100Z',
    bg: 'bg-amber-50', fg: 'text-amber-600',
  },
  dns: {
    path: 'M300-720q-25 0-42.5 17.5T240-660q0 25 17.5 42.5T300-600q25 0 42.5-17.5T360-660q0-25-17.5-42.5T300-720Zm0 400q-25 0-42.5 17.5T240-260q0 25 17.5 42.5T300-200q25 0 42.5-17.5T360-260q0-25-17.5-42.5T300-320ZM160-840h640q17 0 28.5 11.5T840-800v280q0 17-11.5 28.5T800-480H160q-17 0-28.5-11.5T120-520v-280q0-17 11.5-28.5T160-840Zm40 80v200h560v-200H200Zm-40 320h640q17 0 28.5 11.5T840-400v280q0 17-11.5 28.5T800-80H160q-17 0-28.5-11.5T120-120v-280q0-17 11.5-28.5T160-440Zm40 80v200h560v-200H200Zm0-400v200-200Zm0 400v200-200Z',
    bg: 'bg-blue-50', fg: 'text-brand-cyan-text',
  },
}

export default function KpiCard({ title, value, trend, trendPercent, icon }: KpiCardProps) {
  const { path, bg, fg } = iconPaths[icon] ?? iconPaths.warning

  const trendColor = trend === 'up' ? 'text-red-500' : trend === 'down' ? 'text-emerald-500' : 'text-gray-400'
  const trendArrow = trend === 'up' ? '↑' : trend === 'down' ? '↓' : '—'

  return (
    <div className="bg-white rounded-xl shadow-xs border border-gray-100 p-5 flex items-start gap-4">
      <div className={`shrink-0 w-10 h-10 rounded-lg ${bg} flex items-center justify-center`}>
        <svg className={`w-5 h-5 ${fg}`} viewBox="0 -960 960 960" fill="currentColor">
          <path d={path} />
        </svg>
      </div>
      <div className="min-w-0">
        <p className="text-xs font-medium text-gray-500 truncate">{title}</p>
        <p className="text-2xl font-bold text-gray-900 leading-tight mt-0.5">{value}</p>
        {trendPercent > 0 && (
          <p className={`text-xs font-medium mt-1 ${trendColor}`}>
            {trendArrow} {Math.abs(trendPercent)}%
          </p>
        )}
      </div>
    </div>
  )
}
